// @core/stores/billing/BillingCreditStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  CreateCreditGrantRequest,
  CreditGrant,
  VoidCreditGrantRequest,
  VoidCreditGrantResponse,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingCreditStore {
  // List state
  items: CreditGrant[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: CreditGrant | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, CreditGrant>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): CreditGrant | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: CreditGrant | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Grant credit to billing account
   */
  async CreateCreditGrant(billingAccountId: string, data: CreateCreditGrantRequest): Promise<CreditGrant> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateCreditGrant(billingAccountId, data);
      const item = response.credit_grant;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * List credit grants for billing account
   */
  async ListCreditGrants(billingAccountId: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListCreditGrants(billingAccountId, params);

      runInAction(() => {
        this.items = response.credit_grants?.grants ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Void credit grant
   */
  async VoidCreditGrant(id: string, data: VoidCreditGrantRequest): Promise<VoidCreditGrantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.VoidCreditGrant(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const billingCreditStore = new BillingCreditStore();
